import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from '@mui/material'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import commonActions from '../../../actions/common.actions'
import { ErrorCodes } from '../../../proto/generated/common'
import { initiateDematApplication } from '../../../services/apis/tradingAccountProfile'
import { parseErrorFromArrayBuffer } from '../../../components/common/errorParser'
import { TradingAccountProfile } from '../../../proto/generated/trading-bridge'

interface Props {
    open: boolean
    onClose: () => void
    onApplicationStarted?: (profile: TradingAccountProfile) => void
}

const OpenDematAccountDialog = ({ open, onClose, onApplicationStarted }: Props) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const handleOpenAccount = async () => {
        setLoading(true)
        try {
            const profile = await initiateDematApplication()
            setSubmitted(true)
            onApplicationStarted?.(profile)
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Account opening request submitted. Our team will review and activate your account.',
                errorType: 'success'
            })
        } catch (err: any) {
            const { errorCode, message } = parseErrorFromArrayBuffer(err)
            if (errorCode === ErrorCodes.VALIDATION_ERROR && message?.toLowerCase().includes('pending admin review')) {
                setSubmitted(true)
                commonActions.setApiError(dispatch, {
                    error: true,
                    message,
                    errorType: 'info'
                })
                return
            }
            commonActions.setApiError(dispatch, {
                error: true,
                message: message || 'Failed to submit account opening request',
                errorType: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 500 }}>Open Demat Account</DialogTitle>
            <DialogContent>
                <Typography fontSize="14px" color="text.secondary">
                    {submitted
                        ? 'Your account opening request is pending admin review. We will notify you once your account is activated.'
                        : 'Do you want to open a free demat account? We will start your account opening application right away.'}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
                    {submitted ? 'Close' : 'Cancel'}
                </Button>
                {!submitted && (
                    <Button
                        variant="contained"
                        onClick={handleOpenAccount}
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                    >
                        {loading ? <CircularProgress size={18} color="inherit" /> : 'Confirm'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default OpenDematAccountDialog
